import axios from "axios";
import config from "../config";
import { addErrorLogUrl } from "../apiUrls/apiUrls";
import { getAuthHeader, isLoggingOut } from "./authToken";

const { localurl } = config;

// Mirrors the backend ErrorType enum (Invatech.Titanverse.Models.DTO.ErrorLogs).
// The API receives the number, so append new values and never renumber.
export const ErrorType = Object.freeze({
  Unknown: 0,
  Validation: 1,
  Network: 2,
  Server: 3,
  Unauthorized: 4,
  NotFound: 5,
  Timeout: 6,
  Conflict: 7,
  RequestFailed: 8,
});

// Set by httpService on requests it rejects before sending them.
export const MISSING_QUERY_PARAM_ERROR_CODE = "ERR_MISSING_QUERY_PARAM";

const LOGGED_METHODS = ["get", "post", "put", "delete"];
// Only these methods store a Payload; GET and DELETE failures are saved with Payload null.
const PAYLOAD_METHODS = ["post", "put"];
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const GUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
// Keep under the API limits on ErrorLogRequestModel.
const MAX_PAYLOAD_LENGTH = 100000;
const MAX_STRING_VALUE_LENGTH = 5000;
const MAX_PAGE_LENGTH = 1000;
const MAX_API_URL_LENGTH = 2000;
const MAX_EMAIL_LENGTH = 320;
const MAX_DEPTH = 10;
const MAX_LOGS_PER_MINUTE = 30;
const SENSITIVE_KEY =
  /pass(word)?|pwd|secret|token|authorization|api[-_]?key|otp|cvv|cvc|card[-_]?number|^pin$/i;

let contextProvider = null;

// Registered once at startup so this module doesn't import the redux store
// (the store's slices import httpService, which imports this file).
export const setErrorLogContextProvider = (provider) => {
  contextProvider = provider;
};

let windowStart = 0;
let sentInWindow = 0;

// Caps how many logs a burst of failures (e.g. a failing poll) can send.
const takeRateLimitSlot = () => {
  const now = Date.now();
  if (now - windowStart > 60000) {
    windowStart = now;
    sentInWindow = 0;
  }
  if (sentInWindow >= MAX_LOGS_PER_MINUTE) return false;
  sentInWindow += 1;
  return true;
};

const getPath = (url) => String(url || "").split("?")[0];

export const isErrorLogRequest = (url) =>
  getPath(url).toLowerCase().endsWith(addErrorLogUrl().toLowerCase());

const isHighSurrogate = (code) => code >= 0xd800 && code <= 0xdbff;
const isLowSurrogate = (code) => code >= 0xdc00 && code <= 0xdfff;

// An unpaired UTF-16 surrogate is serialized as a lone "\uD83D" escape, which
// the API firewall and System.Text.Json reject, so swap them for U+FFFD.
// (No regex lookbehind: older Safari can't parse it and the module would fail to load.)
const toWellFormed = (value) => {
  const text = String(value ?? "");
  if (typeof text.toWellFormed === "function") return text.toWellFormed();
  let out = "";
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (isHighSurrogate(code) && isLowSurrogate(text.charCodeAt(i + 1))) {
      out += text[i] + text[i + 1];
      i += 1;
    } else if (isHighSurrogate(code) || isLowSurrogate(code)) {
      out += "�";
    } else {
      out += text[i];
    }
  }
  return out;
};

// Cuts to max UTF-16 units without splitting a surrogate pair.
const cut = (value, max) => {
  if (value.length <= max) return value;
  const end = isHighSurrogate(value.charCodeAt(max - 1)) ? max - 1 : max;
  return value.slice(0, end);
};

const truncate = (value, max) => {
  if (value.length <= max) return value;
  const head = cut(value, max);
  return `${head}…[truncated ${value.length - head.length} chars]`;
};

const redact = (value, depth = 0) => {
  if (depth > MAX_DEPTH) return "[Truncated]";
  if (typeof value === "string") return truncate(value, MAX_STRING_VALUE_LENGTH);
  if (typeof value === "bigint") return value.toString();
  if (Array.isArray(value)) return value.map((item) => redact(item, depth + 1));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        SENSITIVE_KEY.test(key) ? "[REDACTED]" : redact(item, depth + 1),
      ]),
    );
  }
  return value;
};

const sanitizeBody = (data) => {
  if (data === undefined || data === null || data === "") return null;
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    return "[FormData omitted]";
  }
  if (
    (typeof Blob !== "undefined" && data instanceof Blob) ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data)
  ) {
    return "[Binary body omitted]";
  }
  if (typeof data === "string") {
    try {
      return redact(JSON.parse(data));
    } catch (_) {
      return truncate(data, MAX_STRING_VALUE_LENGTH);
    }
  }
  return redact(data);
};

const sanitizeUrl = (url) => {
  const value = String(url || "");
  const queryIndex = value.indexOf("?");
  if (queryIndex === -1) return value;
  const query = value
    .slice(queryIndex + 1)
    .split("&")
    .map((pair) => {
      const key = pair.split("=")[0];
      return SENSITIVE_KEY.test(key) ? `${key}=[REDACTED]` : pair;
    })
    .join("&");
  return `${value.slice(0, queryIndex)}?${query}`;
};

// Page and ApiUrl use the API's plain-string pattern (printable ASCII without
// quote or backslash), so percent-encode anything else and cut to length.
const toApiString = (value, max) =>
  toWellFormed(value || "")
    // "u" so a surrogate pair is encoded as one character; halves make encodeURIComponent throw.
    .replace(/[^\x20-\x21\x23-\x5B\x5D-\x7E]/gu, (char) => encodeURIComponent(char))
    .slice(0, max);

const normalizeGuid = (value) => {
  const id = String(value || "").trim();
  return GUID_REGEX.test(id) && id !== EMPTY_GUID ? id : null;
};

export const classifyError = (status, error) => {
  if (status === 400 || status === 422) return ErrorType.Validation;
  if (status === 401 || status === 403) return ErrorType.Unauthorized;
  if (status === 404) return ErrorType.NotFound;
  if (status === 409) return ErrorType.Conflict;
  if (status >= 500) return ErrorType.Server;
  if (status > 0) return ErrorType.Unknown;

  const code = error?.code;
  if (code === MISSING_QUERY_PARAM_ERROR_CODE) return ErrorType.Validation;
  if (
    code === "ECONNABORTED" ||
    code === "ETIMEDOUT" ||
    /timeout of \d+ms exceeded/i.test(error?.message || "")
  ) {
    return ErrorType.Timeout;
  }
  if (code === "ERR_NETWORK" || error?.request) return ErrorType.Network;
  return ErrorType.Unknown;
};

// Payload is only the request body that failed, with sensitive fields redacted.
const buildPayload = (requestConfig) => {
  const body = sanitizeBody(requestConfig.data);
  if (body === null) return null;
  let text;
  if (typeof body === "string") {
    text = body;
  } else {
    try {
      text = JSON.stringify(body);
    } catch (_) {
      text = "[Unserializable body omitted]";
    }
  }
  return toWellFormed(truncate(text, MAX_PAYLOAD_LENGTH - 50));
};

const getRequestPharmacyId = (requestConfig) => {
  const fromParams = Object.entries(requestConfig?.params || {}).find(
    ([key]) => key.toLowerCase() === "pharmacyid",
  )?.[1];
  if (normalizeGuid(fromParams)) return fromParams;
  const query = String(requestConfig?.url || "").split("?")[1] || "";
  const match = query.match(/(?:^|&)pharmacyId=([^&#]*)/i);
  if (!match) return null;
  try {
    return normalizeGuid(decodeURIComponent(match[1]));
  } catch (_) {
    return null;
  }
};

const VALID_ERROR_TYPES = Object.values(ErrorType);

const toOptionalText = (value, max) =>
  typeof value === "string" && value ? cut(toWellFormed(value), max) : null;

const getContext = () => {
  try {
    return (contextProvider && contextProvider()) || {};
  } catch (_) {
    return {};
  }
};

// Reports a failed GET/POST/PUT/DELETE to /ErrorLogs/add-error-log. Fire-and-forget:
// it never throws, never retries, and uses plain axios so the request skips
// the httpService interceptors and cannot log its own failure.
export const reportRequestFailure = ({
  requestConfig,
  status = -1,
  error = null,
  responseData = null,
  errorType,
  correlationId,
}) => {
  try {
    const method = String(requestConfig?.method || "").toLowerCase();
    if (!LOGGED_METHODS.includes(method)) return;
    if (requestConfig.skipErrorLog === true) return;
    if (isErrorLogRequest(requestConfig.url)) return;
    if (axios.isCancel(error) || error?.code === "ERR_CANCELED") return;
    if (isLoggingOut()) return;

    // The endpoint requires a signed-in user; without a token there is nothing to send.
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    if (!takeRateLimitSlot()) return;

    const { userEmail, pharmacyId, companyId } = getContext();
    const resolvedType = errorType ?? classifyError(status, error);
    // Every value below is kept inside ErrorLogRequestModel's schema so the
    // API firewall never rejects the log request.
    const body = {
      payload: PAYLOAD_METHODS.includes(method)
        ? buildPayload(requestConfig)
        : null,
      errorType: VALID_ERROR_TYPES.includes(resolvedType)
        ? resolvedType
        : ErrorType.Unknown,
      page: toApiString(window.location.pathname, MAX_PAGE_LENGTH),
      apiUrl: toApiString(
        sanitizeUrl(`${requestConfig.baseURL || ""}${requestConfig.url || ""}`),
        MAX_API_URL_LENGTH,
      ),
      userEmail: toOptionalText(userEmail, MAX_EMAIL_LENGTH),
      pharmacyId: normalizeGuid(getRequestPharmacyId(requestConfig) ?? pharmacyId),
      companyId: normalizeGuid(companyId),
    };

    axios
      .post(`${localurl}${addErrorLogUrl()}`, body, {
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      })
      .catch(() => {});
  } catch (_) {
    // Error logging must never affect the app.
  }
};
